/**
 * Composant de gestion du compte
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Gestion du profil utilisateur et suppression du compte.
 */

  // Client Component pour Next.js

import { useState } from 'react';
import { useAuth, useSecurity } from '../../hooks';
import { ChangeEmailForm } from '../forms/ChangeEmailForm';
import { SecurityAlert, useSecurityAlert } from '../ui/SecurityAlert';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { authApi } from '../../../infrastructure/api';

/**
 * Props du composant AccountSettings
 */
export interface AccountSettingsProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Callback après mise à jour du profil */
  onProfileUpdated?: () => void;
  /** Callback après changement d'email */
  onEmailChanged?: () => void;
  /** Callback après suppression du compte */
  onAccountDeleted?: () => void;
  /** Afficher la section de suppression du compte */
  showDeleteSection?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant AccountSettings
 *
 * @example
 * ```tsx
 * function AccountPage() {
 *   const { user } = useAuth();
 *   const router = useNavigate();
 *
 *   return (
 *     <AccountSettings
 *       userId={user.id}
 *       showDeleteSection={true}
 *       onAccountDeleted={() => navigate('/login')}
 *     />
 *   );
 * }
 * ```
 */
export function AccountSettings({
  userId,
  onProfileUpdated,
  onEmailChanged,
  onAccountDeleted,
  showDeleteSection = true,
  className = '',
}: AccountSettingsProps) {
  const { user, updateProfile, isLoading, error } = useAuth();
  const {deleteAccount}=authApi
  const { alert, showAlert, hideAlert } = useSecurityAlert();

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    telephone: user?.telephone || '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /**
   * Valider le formulaire de profil
   */
  const validateProfileForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.nom.trim()) {
      errors.nom = 'Le nom est requis';
    }

    if (!formData.prenom.trim()) {
      errors.prenom = 'Le prénom est requis';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Mettre à jour le profil
   */
  const handleUpdateProfile = async () => {
    if (!validateProfileForm()) return;

    try {
      await updateProfile({
        idUtilisateur: userId,
        nom: formData.nom,
        prenom: formData.prenom,
        phoneNumber: formData.telephone || undefined,
      });

      setEditMode(false);
      showAlert("info",'Profil mis à jour avec succès', 'success');
      onProfileUpdated?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de mise à jour du profil';
      showAlert('error', message, 'error');
    }
  };

  /**
   * Annuler l'édition du profil
   */
  const handleCancelEdit = () => {
    setFormData({
      nom: user?.nom || '',
      prenom: user?.prenom || '',
      telephone: user?.telephone || '',
    });
    setFormErrors({});
    setEditMode(false);
  };

  /**
   * Gérer le changement d'email réussi
   */
  const handleEmailChangeSuccess = () => {
    setShowEmailForm(false);
    showAlert("error",'Un email de vérification a été envoyé à votre nouvelle adresse', 'info');
    onEmailChanged?.();
  };

  /**
   * Supprimer le compte
   */
  const handleDeleteAccount = async () => {
    setDeleteError(null);

    if (!deletePassword) {
      setDeleteError('Le mot de passe est requis pour confirmer la suppression');
      return;
    }

    try {
      await deleteAccount({
        idUtilisateur: userId,
        motDePasse: deletePassword,
        confirmation: 'Suppression du compte',
      });

      showAlert('success','Compte supprimé avec succès', 'info');
      onAccountDeleted?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de suppression du compte';
      setDeleteError(message);
      showAlert('error','error');
    }
  };

  /**
   * Formater la date
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(date);
  };

  return (
    <div className={`w-full max-w-4xl ${className}`}>
      {/* Titre */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-content">
          Paramètres du compte
        </h2>
        <p className="mt-1 text-sm text-content-muted">
          Gérez les informations de votre compte
        </p>
      </div>

      {/* Alerte */}
      {alert && (
        <div className="mb-6">
          <SecurityAlert
            {...alert}
          />
        </div>
      )}

      {/* Erreur globale */}
      {error && (
        <div
          className="mb-6 p-4 bg-danger-light border border-danger rounded-lg"
          role="alert"
        >
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Informations de profil */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-content">
              Informations de profil
            </h3>
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-primary-text hover:bg-surface-raised rounded-lg transition-colors disabled:opacity-50"
              >
                Modifier
              </button>
            )}
          </div>

          {!editMode ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-sm font-medium text-content-subtle">Prénom</span>
                <span className="text-sm text-content">{user?.prenom || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-sm font-medium text-content-subtle">Nom</span>
                <span className="text-sm text-content">{user?.nom || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-sm font-medium text-content-subtle">Téléphone</span>
                <span className="text-sm text-content">{user?.telephone || 'Non renseigné'}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm font-medium text-content-subtle">Email</span>
                <span className="text-sm text-content">{user?.email || 'N/A'}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label htmlFor="prenom" className="block text-sm font-medium text-content-muted mb-1">
                  Prénom
                </label>
                <input
                  id="prenom"
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => {
                    setFormData({ ...formData, prenom: e.target.value });
                    if (formErrors.prenom) {
                      setFormErrors({ ...formErrors, prenom: '' });
                    }
                  }}
                  disabled={isLoading}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                    formErrors.prenom
                      ? 'border-danger'
                      : 'border-border'
                  } bg-surface text-content`}
                />
                {formErrors.prenom && (
                  <p className="mt-1 text-sm text-danger">{formErrors.prenom}</p>
                )}
              </div>

              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-content-muted mb-1">
                  Nom
                </label>
                <input
                  id="nom"
                  type="text"
                  value={formData.nom}
                  onChange={(e) => {
                    setFormData({ ...formData, nom: e.target.value });
                    if (formErrors.nom) {
                      setFormErrors({ ...formErrors, nom: '' });
                    }
                  }}
                  disabled={isLoading}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                    formErrors.nom
                      ? 'border-danger'
                      : 'border-border'
                  } bg-surface text-content`}
                />
                {formErrors.nom && (
                  <p className="mt-1 text-sm text-danger">{formErrors.nom}</p>
                )}
              </div>

              <div>
                <label htmlFor="telephone" className="block text-sm font-medium text-content-muted mb-1">
                  Téléphone
                </label>
                <input
                  id="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => {
                    setFormData({ ...formData, telephone: e.target.value });
                    if (formErrors.telephone) {
                      setFormErrors({ ...formErrors, telephone: '' });
                    }
                  }}
                  disabled={isLoading}
                  placeholder="+33 6 12 34 56 78"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                    formErrors.telephone
                      ? 'border-danger'
                      : 'border-border'
                  } bg-surface text-content`}
                />
                {formErrors.telephone && (
                  <p className="mt-1 text-sm text-danger">{formErrors.telephone}</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-border text-content-muted bg-surface hover:bg-surface-raised font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpdateProfile}
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-accent hover:bg-accent text-content-inverted font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Informations du compte */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-content mb-4">
            Informations du compte
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <span className="text-sm font-medium text-content-subtle">ID utilisateur</span>
              <span className="text-sm font-mono text-content">{user?.id || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border">
              <span className="text-sm font-medium text-content-subtle">Date de création</span>
              <span className="text-sm text-content">
                {/* {formatDate(user?.dateCreation?.toISOString())} */}
{formatDate(user?.dateCreation ? (user.dateCreation instanceof Date ? user.dateCreation.toISOString() : user.dateCreation) : new Date().toISOString())}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-medium text-content-subtle">Rôle</span>
              <span className="px-2 py-1 bg-surface-overlay rounded text-xs font-medium text-content-muted">
                {user?.role || 'UTILISATEUR'}
              </span>
            </div>
          </div>
        </div>

        {/* Changer l'email */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-content mb-4">
            Adresse email
          </h3>

          {!showEmailForm ? (
            <div>
              <p className="text-sm text-content-muted mb-4">
                Email actuel : <span className="font-medium text-content">{user?.email}</span>
              </p>
              <button
                onClick={() => setShowEmailForm(true)}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-primary-text hover:bg-surface-raised rounded-lg transition-colors disabled:opacity-50"
              >
                Changer l'adresse email
              </button>
            </div>
          ) : (
            <ChangeEmailForm
              userId={userId}
              currentEmail={user?.email || ''}
              onSuccess={handleEmailChangeSuccess}
              onError={(error) => showAlert('error',error)}
              showCancel={true}
              onCancel={() => setShowEmailForm(false)}
            />
          )}
        </div>

        {/* Zone dangereuse */}
        {showDeleteSection && (
          <div className="bg-danger-light border border-danger rounded-lg p-6">
            <div className="flex items-start mb-4">
              <svg
                className="w-6 h-6 text-danger mr-3 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-danger">
                  Zone de danger
                </h3>
                <p className="mt-1 text-sm text-danger">
                  La suppression de votre compte est irréversible. Toutes vos données seront
                  définitivement supprimées.
                </p>
              </div>
            </div>

            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isLoading}
                className="px-4 py-2 bg-danger hover:bg-danger text-content-inverted text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Supprimer mon compte
              </button>
            ) : (
              <div className="bg-surface rounded-lg p-4">
                <p className="text-sm text-content mb-4 font-semibold">
                  Confirmez la suppression en entrant votre mot de passe :
                </p>

                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => {
                    setDeletePassword(e.target.value);
                    setDeleteError(null);
                  }}
                  disabled={isLoading}
                  className={`w-full px-4 py-2 border rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-danger disabled:opacity-50 ${
                    deleteError
                      ? 'border-danger'
                      : 'border-border'
                  } bg-surface text-content`}
                  placeholder="Votre mot de passe"
                />

                {deleteError && (
                  <p className="mb-3 text-sm text-danger">{deleteError}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletePassword('');
                      setDeleteError(null);
                    }}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 border border-border text-content-muted bg-surface hover:bg-surface-raised font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isLoading || !deletePassword}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-danger hover:bg-danger text-content-inverted font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Suppression...
                      </>
                    ) : (
                      'Confirmer la suppression'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
